// src/components/ReportForm.js
import React, { useState } from 'react';
import { useTranslation } from '../i18n/i18n';
import {
  Box, Typography, TextField, Button, Paper, Alert,
  CircularProgress, Avatar, Divider, Dialog, DialogTitle, DialogContent,
  DialogActions, Checkbox, FormControlLabel, IconButton
} from '@mui/material';
import { motion } from 'framer-motion';
import API_ENDPOINTS from '../config/apiConfig';
import FixedSnackbar from './FixedSnackbar';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';

function ReportForm() {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    location: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.description.trim()) {
      setSnackbar({ open: true, message: t('report_error_description_required'), severity: 'warning' });
      return;
    }

    setConfirmOpen(true);
  };

  const handleConfirmSubmit = async () => {
    if (!acknowledged) {
      setSnackbar({ open: true, message: t('report_confirm_acknowledge_required'), severity: 'warning' });
      return;
    }

    setConfirmOpen(false);
    setAcknowledged(false);
    setSubmitting(true);
    try {
      const response = await fetch(API_ENDPOINTS.REPORTS_SUBMIT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSnackbar({ open: true, message: t('report_success_submitted'), severity: 'success' });
        setFormData({ subject: '', description: '', location: '' });
      } else {
        const data = await response.json();
        setSnackbar({ open: true, message: data.message || t('report_error_submit_failed'), severity: 'error' });
      }
    } catch (error) {
      console.error('Error submitting report:', error);
      setSnackbar({ open: true, message: t('report_error_generic'), severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, py: { xs: 3, sm: 4 } }}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        {/* Page Header */}
        <Box sx={{ mb: { xs: 3, sm: 4 } }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            {t('report_title') || 'Confidential Reporting'}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 640 }}>
            {t('report_subtitle') || 'Report workplace behaviors or incidents that need investigation. Your report is anonymous.'}
          </Typography>
        </Box>

        {/* Legend / Info Card */}
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: 2.5,
            background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.08) 0%, rgba(255, 87, 34, 0.05) 100%)',
            border: '1px solid',
            borderColor: 'warning.light',
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'warning.main', color: 'warning.contrastText' }}>
              <InfoOutlinedIcon sx={{ fontSize: 18 }} />
            </Avatar>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'warning.dark', mb: 0.5 }}>
                {t('report_legend_title')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                {t('report_legend_description')}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Form */}
        <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, border: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Avatar sx={{ width: 36, height: 36, bgcolor: 'warning.main', color: 'warning.contrastText' }}>
              <ReportProblemIcon sx={{ fontSize: 20 }} />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('report_form_title')}
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label={t('report_field_subject')}
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              placeholder={t('report_field_subject_placeholder')}
              sx={{ mb: 2.5 }}
              inputProps={{ maxLength: 200 }}
              helperText={`${formData.subject.length}/200`}
            />

            <TextField
              fullWidth
              multiline
              rows={5}
              label={t('report_field_description')}
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder={t('report_field_description_placeholder')}
              sx={{ mb: 2.5 }}
              required
              inputProps={{ maxLength: 5000 }}
              helperText={`${formData.description.length}/5000`}
            />

            <TextField
              fullWidth
              label={t('report_field_location')}
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder={t('report_field_location_placeholder')}
              sx={{ mb: 3 }}
              inputProps={{ maxLength: 200 }}
            />

            <Alert severity="info" sx={{ mb: 3, borderRadius: 1.5 }}>
              {t('report_anonymity_notice')}
            </Alert>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                py: 1.5,
                boxShadow: 'none',
                '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }
              }}
            >
              {submitting ? t('please_wait') || 'Sending...' : t('report_button_submit')}
            </Button>
          </form>
        </Paper>
      </motion.div>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => { setConfirmOpen(false); setAcknowledged(false); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'warning.main', color: 'warning.contrastText' }}>
              <ReportProblemIcon sx={{ fontSize: 18 }} />
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('report_confirm_title')}
            </Typography>
          </Box>
          <IconButton onClick={() => { setConfirmOpen(false); setAcknowledged(false); }} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 1.5 }}>
            {t('report_confirm_warning')}
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('report_confirm_acknowledge_text')}
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                color="warning"
              />
            }
            label={
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {t('report_confirm_checkbox')}
              </Typography>
            }
          />
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={() => { setConfirmOpen(false); setAcknowledged(false); }} sx={{ textTransform: 'none' }}>
            {t('report_confirm_cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmSubmit}
            disabled={!acknowledged}
            sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
          >
            {t('report_confirm_submit')}
          </Button>
        </DialogActions>
      </Dialog>

      <FixedSnackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </Box>
  );
}

export default ReportForm;
